import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Map as MapIcon } from 'lucide-react';
import type { CountryRegionToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useCountryRegionsList, useRegionCountries, useCountryRegionMutations } from '~/hooks/use-country-regions';
import { tokenToForm, type CountryRegionFormValues } from '~/lib/schemas/country-region';
import { CountryRegionEditDialog } from './country-region-edit-dialog';

/** Regions (Νομοί) CRUD surface — parity with the legacy admin grid (Country/Name/Code/Visible/Sort). */
export function CountryRegionsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCountryRegionsList();
	const { data: countries = [] } = useRegionCountries();
	const { remove } = useCountryRegionMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<CountryRegionFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<CountryRegionToken | null>(null);

	const countryMap = useMemo(() => new Map(countries.map(c => [c.id, c.nm])), [countries]);
	const countryOf = (r: CountryRegionToken) => countryMap.get(r.cid) ?? '';

	const columns = useMemo<SystemGridColumn<CountryRegionToken>[]>(
		() => [
			{
				key: 'country',
				header: t('system.countryRegions.columns.country'),
				width: '200px',
				sortValue: r => countryOf(r),
				exportValue: r => countryOf(r),
				render: r => <span className="truncate text-sm text-foreground-muted">{countryOf(r) || '—'}</span>
			},
			{
				key: 'name',
				header: t('system.countryRegions.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: r => r.nm,
				exportValue: r => r.nm,
				render: r => <span className="truncate text-sm font-medium text-foreground">{r.nm}</span>
			},
			{
				key: 'code',
				header: t('system.countryRegions.columns.code'),
				width: '120px',
				sortValue: r => r.cd ?? '',
				exportValue: r => r.cd ?? '',
				render: r => <span className="font-mono text-xs text-foreground-muted">{r.cd || '—'}</span>
			},
			{
				key: 'erpCode',
				header: t('system.fields.erpCode'),
				width: '120px',
				defaultVisible: false,
				sortValue: r => r.erpcd ?? '',
				exportValue: r => r.erpcd ?? '',
				render: r => <span className="font-mono text-xs text-foreground-muted">{r.erpcd || '—'}</span>
			},
			{
				key: 'visible',
				header: t('system.countryRegions.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => (r.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.countryRegions.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, countryMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<CountryRegionToken>
				gridId="country-regions"
				title={t('system.countryRegions.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => `${r.nm ?? ''} ${r.cd ?? ''} ${countryOf(r)}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<MapIcon size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.countryRegions.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={r => {
					setEditing(tokenToForm(r));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="country-regions"
			/>

			<CountryRegionEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} countries={countries} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.countryRegions.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.countryRegions.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
